import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
} from "lucide-react";
import apiClient from "../api/client";
import "./DailyReportPage.css";

const STATUS = {
  BREACH: ["פריצה", "breach"],
  WARNING: ["אזהרה", "warning"],
  SAFE: ["תקין", "safe"],
  RELEASE_ELIGIBLE: ["זכאי לשחרור", "release"],
  NO_COLLATERAL: ["ללא בטוחה", "none"],
  NO_PRICE: ["אין מחיר", "none"],
};

const severity = {
  BREACH: 0,
  WARNING: 1,
  SAFE: 2,
  RELEASE_ELIGIBLE: 3,
  NO_COLLATERAL: 4,
  NO_PRICE: 5,
};

const SORT_OPTIONS = [
  ["severity", "חומרה"],
  ["distance", "% לפריצה"],
  ["debt", "חוב נטו"],
];

function formatILS(value, compact = false) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  if (compact && Math.abs(value) >= 1_000_000_000) {
    return `₪${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (compact && Math.abs(value) >= 1_000_000) {
    return `₪${(value / 1_000_000).toFixed(1)}M`;
  }
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPct(value, digits = 1) {
  return value == null || Number.isNaN(Number(value))
    ? "—"
    : `${Number(value).toFixed(digits)}%`;
}

function formatNumber(value) {
  return value == null || Number.isNaN(Number(value))
    ? "—"
    : Number(value).toLocaleString("he-IL");
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("he-IL");
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return date.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
}

function dateString(date) {
  return date.toISOString().slice(0, 10);
}

function hebrewDate(value) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("he-IL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatChange(value, formatter, suffix = "") {
  if (value == null || Number.isNaN(Number(value))) return null;
  const prefix = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${prefix}${formatter(Math.abs(value))}${suffix}`;
}

function distanceClass(value) {
  if (value == null) return "";
  if (value < 5) return "value-red";
  if (value < 10) return "value-amber";
  return "value-green";
}

function ltvClass(value) {
  if (value == null) return "";
  if (value >= 100) return "value-red";
  if (value >= 85) return "value-amber";
  return "";
}

function NavBar({ currentDate, generatedAt, isLive, onDateChange, onDownload }) {
  const navigate = useNavigate();
  const today = dateString(new Date());
  const dateInputRef = React.useRef(null);
  const changeDate = (days) => {
    const next = new Date(`${currentDate}T12:00:00`);
    next.setDate(next.getDate() + days);
    const value = dateString(next);
    if (value <= today) onDateChange(value);
  };

  return (
    <header className="dr-header">
      <div className="dr-heading">
        <button
          type="button"
          className="dr-back-button"
          onClick={() => navigate("/reports")}
          aria-label="חזרה לדוחות"
        >
          <ChevronRight size={18} />
        </button>
        <div>
          <h1>דוח יומי</h1>
          <p>תמונת חשיפה — כל התיקים</p>
        </div>
        <span className={`dr-live-status ${isLive ? "is-live" : ""}`}>
          <i />
          {isLive ? `עדכון חי${generatedAt ? ` · ${formatTime(generatedAt)}` : ""}` : "דוח סגירה"}
        </span>
      </div>

      <div className="dr-header-actions">
        <div className="dr-date-control">
          <button type="button" onClick={() => changeDate(-1)} aria-label="יום קודם">
            <ChevronRight size={17} />
          </button>
          <button
            type="button"
            className="dr-date-value"
            onClick={() => dateInputRef.current?.showPicker?.()}
          >
            {hebrewDate(currentDate)}
          </button>
          <input
            ref={dateInputRef}
            className="dr-date-input"
            type="date"
            value={currentDate}
            max={today}
            onChange={(event) => onDateChange(event.target.value)}
            aria-label="בחר תאריך דוח"
          />
          <button type="button" onClick={() => changeDate(1)} disabled={currentDate >= today} aria-label="יום הבא">
            <ChevronLeft size={17} />
          </button>
        </div>
        <button type="button" className="dr-download-button" onClick={onDownload}>
          <Download size={16} />
          הורד PDF
        </button>
      </div>
    </header>
  );
}

function MarketTicker({ marketData }) {
  const [open, setOpen] = useState(false);
  const highlights = useMemo(() => {
    const stock = marketData?.stocks?.[0];
    const fx = marketData?.fxRates?.[0];
    const rate = marketData?.baseRates?.[0];
    return [
      stock && { key: `stock-${stock.symbol}`, label: stock.symbol, value: stock.close?.toFixed(2), change: stock.changePercent, href: `/asset/stock/${stock.symbol}` },
      fx && { key: `fx-${fx.pair}`, label: fx.pair, value: fx.rate?.toFixed(3), change: fx.changePercent, href: `/asset/fx/${fx.pair}` },
      rate && { key: `rate-${rate.name}`, label: rate.name, value: rate.value == null ? "—" : `${rate.value.toFixed(2)}%`, href: `/asset/base-rate/${rate.name}` },
    ].filter(Boolean);
  }, [marketData]);

  if (!highlights.length) return null;

  return (
    <section className="dr-market-strip" aria-label="נתוני שוק">
      <div className="dr-market-highlights">
        {highlights.map((item) => (
          <Link className="dr-market-highlight" to={item.href} key={item.key}>
            <span>{item.label}</span>
            <strong dir="ltr">{item.value || "—"}</strong>
            {item.change != null && (
              <em className={item.change >= 0 ? "positive" : "negative"} dir="ltr">
                {item.change >= 0 ? "+" : ""}{item.change.toFixed(1)}%
              </em>
            )}
          </Link>
        ))}
      </div>
      <button type="button" className="dr-market-more" onClick={() => setOpen((value) => !value)}>
        כל נתוני השוק {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>
      {open && <MarketDetails marketData={marketData} />}
    </section>
  );
}

function MarketDetails({ marketData }) {
  const rows = [
    ...(marketData?.stocks || []).map((item) => ({ label: item.symbol, value: item.close?.toFixed(2), change: item.changePercent, href: `/asset/stock/${item.symbol}` })),
    ...(marketData?.fxRates || []).map((item) => ({ label: item.pair, value: item.rate?.toFixed(3), change: item.changePercent, href: `/asset/fx/${item.pair}` })),
    ...(marketData?.baseRates || []).map((item) => ({ label: item.name, value: item.value == null ? "—" : `${item.value.toFixed(2)}%`, href: `/asset/base-rate/${item.name}` })),
  ];
  return (
    <div className="dr-market-details">
      {rows.map((item) => (
        <Link to={item.href} key={`${item.label}-${item.value}`}>
          <span>{item.label}</span>
          <strong dir="ltr">{item.value || "—"}</strong>
          {item.change != null && <em className={item.change >= 0 ? "positive" : "negative"} dir="ltr">{item.change >= 0 ? "+" : ""}{item.change.toFixed(1)}%</em>}
        </Link>
      ))}
    </div>
  );
}

function ChangeNote({ value, type = "currency" }) {
  if (value == null) return <span className="dr-change-note muted">אין נתון להשוואה</span>;
  const isPositive = value >= 0;
  const formatted = type === "points"
    ? formatChange(value, (number) => number.toFixed(1), " נק׳")
    : formatChange(value, (number) => formatILS(number, true));
  return <span className={`dr-change-note ${isPositive ? "positive" : "negative"}`} dir="ltr">{formatted}</span>;
}

function PortfolioMetrics({ summary, freeShareTotals, comparison }) {
  if (!summary) return null;
  const ltv = Number(summary.weightedLTV);
  const width = Number.isFinite(ltv) ? Math.max(0, Math.min(100, ltv)) : 0;
  const freePct = Number(freeShareTotals?.freePct);
  return (
    <section className="dr-metrics-grid">
      <article className="dr-metric-card dr-ltv-card">
        <div className="dr-metric-topline"><span>LTV משוקלל</span><b className={ltvClass(ltv)}>{formatPct(ltv)}</b></div>
        <div className="dr-ltv-value" dir="ltr">{formatPct(ltv)}</div>
        <div className="dr-metric-compare"><ChangeNote value={comparison ? ltv - comparison.weightedLTV : null} type="points" /><span>מול הדוח הקודם</span></div>
        <div className="dr-ltv-track"><span style={{ width: `${width}%` }} /></div>
        <div className="dr-ltv-footer"><span dir="ltr">VTL {formatPct(summary.weightedVTL)}</span><span>חוב מול בטחונות מתומחרים</span></div>
      </article>
      <article className="dr-metric-card">
        <span>חוב נטו כולל</span>
        <strong dir="ltr">{formatILS(summary.totalNetDebt, true)}</strong>
        <div className="dr-metric-compare"><ChangeNote value={comparison ? summary.totalNetDebt - comparison.totalNetDebt : null} /><span>מול הדוח הקודם</span></div>
      </article>
      <article className="dr-metric-card">
        <span>שווי בטחונות כולל</span>
        <strong dir="ltr">{formatILS(summary.totalCollateralValue, true)}</strong>
        <div className="dr-metric-compare"><ChangeNote value={comparison ? summary.totalCollateralValue - comparison.totalCollateralValue : null} /><span>מול הדוח הקודם</span></div>
      </article>
      <article className="dr-metric-card dr-free-share-card">
        <div className="dr-metric-topline"><span>מניות זמינות</span>{Number.isFinite(freePct) && <b dir="ltr">{formatPct(freePct)}</b>}</div>
        <strong dir="ltr">{formatILS(summary.totalFreeShareValue, true)}</strong>
        <div className="dr-share-track"><span style={{ width: `${Math.max(0, Math.min(100, freePct || 0))}%` }} /></div>
        <div className="dr-ltv-footer"><span>זמין {formatNumber(freeShareTotals?.freeShares)} יח׳</span><span>מוגבל {formatNumber(freeShareTotals?.restrictedShares || 0)} יח׳</span><span>משועבד {formatNumber(freeShareTotals?.pledgedShares)} יח׳</span></div>
      </article>
    </section>
  );
}

function BasketDetails({ basket }) {
  const needsRemedy = basket.cashShortfall > 0 || basket.sharesNeededToRemedy > 0;
  return (
    <div className="dr-basket-details">
      <div className="dr-details-grid">
        <div className="dr-detail-block">
          <h4>חוב</h4>
          <DetailRow label="קרן נותרת" value={formatILS(basket.totalPrincipal)} />
          <DetailRow label="ריבית נצברת" value={formatILS(basket.totalAccruedInterest)} />
          <DetailRow label="פיקדון מזומן" value={formatILS(basket.totalCashDeposit)} />
          <DetailRow label="חוב נטו" value={formatILS(basket.netDebt)} total />
        </div>
        <div className="dr-detail-block">
          <h4>בטוחות ומדדי סיכון</h4>
          {basket.collateralItems?.map((item, index) => <DetailRow key={`${item.symbol}-${index}`} label={<Link to={`/asset/stock/${item.symbol}`}>{item.symbol} ({formatNumber(item.shareCount)})</Link>} value={formatILS(item.value, true)} />)}
          <DetailRow label="שווי בטחונות" value={formatILS(basket.totalCollateralValue, true)} total />
          <DetailRow label="סף הפרה" value={formatPct(basket.ltvBreachThreshold)} />
          <DetailRow label="מרחק מהפרה" value={formatPct(basket.pctToBreachLTV)} valueClass={distanceClass(basket.pctToBreachLTV)} />
        </div>
        <div className="dr-detail-block">
          <h4>תיקון</h4>
          {basket.cashShortfall > 0 && <DetailRow label="חוסר בטחונות" value={formatILS(basket.cashShortfall)} valueClass="value-red" />}
          {basket.sharesNeededToRemedy > 0 && <DetailRow label="השלמה במניות" value={`${formatNumber(basket.sharesNeededToRemedy)} מניות`} valueClass="value-red" />}
          {basket.surplusShares > 0 && <DetailRow label="מניות לשחרור" value={formatNumber(Math.floor(basket.surplusShares))} valueClass="value-green" />}
          {!needsRemedy && !basket.surplusShares && <p className="dr-no-remedy">אין צורך בתיקון</p>}
        </div>
      </div>
      <CovenantChips basket={basket} />
      {!basket.isSingleton && basket.loans?.length > 0 && <LoansSubTable loans={basket.loans} />}
    </div>
  );
}

function DetailRow({ label, value, total = false, valueClass = "" }) {
  return <div className={`dr-detail-row ${total ? "is-total" : ""}`}><span>{label}</span><strong className={valueClass}>{value}</strong></div>;
}

function CovenantChips({ basket }) {
  const chips = [];
  if (basket.consecutiveBreachDays > 0) chips.push([`${basket.consecutiveBreachDays} ימי פריצה`, "red"]);
  if (basket.remedyPeriodDays) chips.push([`תקופת תיקון: ${basket.remedyPeriodDays} ימים`, "neutral"]);
  if (basket.releaseEligible) chips.push(["זכאי לשחרור", "green"]);
  if (basket.dividendReleaseEligible) chips.push(["זכאי לשחרור דיבידנד", "green"]);
  if (basket.gracePeriodActive) chips.push(["תקופת חסד פעילה", "blue"]);
  if (basket.activePenalties?.length) basket.activePenalties.forEach((penalty) => chips.push([`קנס +${penalty.penaltyRate}%`, "red"]));
  if (!chips.length) return null;
  return <div className="dr-covenant-chips">{chips.map(([label, tone], index) => <span className={`dr-chip ${tone}`} key={`${label}-${index}`}>{label}</span>)}</div>;
}

function LoansSubTable({ loans }) {
  return (
    <div className="dr-loans-section">
      <h4>הלוואות במסגרת</h4>
      <div className="dr-table-scroll"><table className="dr-loans-table"><thead><tr><th>מלווה</th><th>לווה</th><th>קרן נותרת</th><th>פיקדון</th><th>פירעון</th><th>ריבית</th></tr></thead><tbody>
        {loans.map((loan) => <tr key={loan.loanId}><td>{loan.lender}</td><td>{loan.borrowerCompany}</td><td>{formatILS(loan.remainingPrincipal)}</td><td>{formatILS(loan.cashDepositAmount)}</td><td>{formatDate(loan.maturityDate)}</td><td>{formatPct(loan.interestRate, 2)}</td></tr>)}
      </tbody></table></div>
    </div>
  );
}

function BasketTable({ baskets }) {
  const [expandedRow, setExpandedRow] = useState(null);
  return (
    <div className="dr-basket-table-wrap">
      <div className="dr-table-scroll"><table className="dr-baskets-table"><thead><tr><th>מסגרת</th><th>LTV</th><th>חוב נטו</th><th>בטחונות</th><th>מרחק לפריצה</th><th /></tr></thead><tbody>
        {baskets.map((basket) => {
          const [, statusClass] = STATUS[basket.ltvStatus] || STATUS.NO_COLLATERAL;
          const expanded = expandedRow === basket.basketId;
          return <React.Fragment key={basket.basketId}>
            <tr className={`dr-basket-row status-${statusClass} ${expanded ? "is-expanded" : ""}`} onClick={() => setExpandedRow(expanded ? null : basket.basketId)}>
              <td><div className="dr-basket-name"><i className={`dr-status-dot ${statusClass}`} /><div><strong>{basket.isSingleton ? basket.lender : basket.basketName}</strong><span>{basket.lender}{basket.borrowerCompany ? ` · ${basket.borrowerCompany}` : ""}</span></div></div></td>
              <td><strong className={ltvClass(basket.currentLTV)} dir="ltr">{formatPct(basket.currentLTV)}</strong><small dir="ltr">VTL {formatPct(basket.vtl)}</small></td>
              <td dir="ltr">{formatILS(basket.netDebt, true)}</td>
              <td dir="ltr">{formatILS(basket.totalCollateralValue, true)}</td>
              <td><div className="dr-distance"><span><i><b style={{ width: `${Math.max(0, Math.min(100, Math.abs(Number(basket.pctToBreachLTV) || 0)))}%` }} /></i><strong className={distanceClass(basket.pctToBreachLTV)} dir="ltr">{formatPct(basket.pctToBreachLTV)}</strong></span></div></td>
              <td className="dr-row-chevron">{expanded ? <ChevronUp size={17} /> : <ChevronDown size={17} />}</td>
            </tr>
            {expanded && <tr className="dr-expanded-row"><td colSpan="6"><BasketDetails basket={basket} /></td></tr>}
          </React.Fragment>;
        })}
      </tbody></table></div>
      {!baskets.length && <div className="dr-empty-state">אין מסגרות להצגה במסנן זה</div>}
    </div>
  );
}

function BasketSection({ baskets, allBaskets, filter, onFilterChange, sortBy, onSortChange }) {
  const [sortOpen, setSortOpen] = useState(false);
  const lenders = new Set(allBaskets.map((basket) => basket.lender).filter(Boolean)).size;
  return (
    <section className="dr-baskets-panel">
      <header className="dr-baskets-header">
        <div><h2>מסגרות אשראי</h2><span>{allBaskets.length} מסגרות · {lenders} גופים מממנים</span></div>
        <div className="dr-basket-controls">
          <div className="dr-filter-tabs">
            {[['all', 'הכל'], ['risk', 'בסיכון'], ['safe', 'תקין']].map(([value, label]) => <button type="button" key={value} className={filter === value ? "active" : ""} onClick={() => onFilterChange(value)}>{label}</button>)}
          </div>
          <div className="dr-sort-dropdown">
            <button type="button" className="dr-sort-button" onClick={() => setSortOpen((value) => !value)}><ArrowUpDown size={14} />{SORT_OPTIONS.find(([value]) => value === sortBy)?.[1]}</button>
            {sortOpen && <div className="dr-sort-menu">{SORT_OPTIONS.map(([value, label]) => <button type="button" key={value} className={sortBy === value ? "active" : ""} onClick={() => { onSortChange(value); setSortOpen(false); }}>{label}</button>)}</div>}
          </div>
        </div>
      </header>
      <BasketTable baskets={baskets} />
    </section>
  );
}

function AlertsPanel({ alerts }) {
  return (
    <section className="dr-side-card">
      <div className="dr-side-title"><h3>התראות פעילות</h3><b>{alerts.length}</b></div>
      {alerts.length ? <div className="dr-alert-list">{alerts.map((alert, index) => {
        const tone = alert.alertType === "BREACH_COUNTDOWN" || alert.alertType === "PENALTY_ACTIVE" ? "red" : "amber";
        return <div className={`dr-alert ${tone}`} key={`${alert.message}-${index}`}><i /><div><strong>{alert.basketName}</strong><span>{alert.message}</span></div></div>;
      })}</div> : <p className="dr-side-empty">אין התראות פעילות</p>}
    </section>
  );
}

function LtvTrend({ history, currentSummary, currentDate }) {
  const pointsByDate = new Map(history.map((item) => [item.date, item]));
  pointsByDate.set(currentDate, { date: currentDate, weightedLTV: currentSummary?.weightedLTV });
  const values = [...pointsByDate.values()]
    .filter((item) => item.weightedLTV != null)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-30);
  if (values.length < 2) return <section className="dr-side-card"><div className="dr-side-title"><h3>מגמת LTV</h3><span>30 יום</span></div><p className="dr-side-empty">מגמה תופיע לאחר שמירת שני דוחות לפחות</p></section>;
  const numbers = values.map((item) => Number(item.weightedLTV));
  const min = Math.min(...numbers, 85);
  const max = Math.max(...numbers, 85);
  const range = max - min || 1;
  const points = numbers.map((value, index) => `${(index / (numbers.length - 1)) * 300},${12 + ((max - value) / range) * 66}`);
  const line = `M${points.join(" L")}`;
  const area = `${line} L300,90 L0,90 Z`;
  return <section className="dr-side-card dr-trend-card"><div className="dr-side-title"><h3>מגמת LTV</h3><span>30 יום</span></div><svg viewBox="0 0 300 96" preserveAspectRatio="none" aria-label="מגמת LTV"><line x1="0" y1={12 + ((max - 85) / range) * 66} x2="300" y2={12 + ((max - 85) / range) * 66} /><path className="dr-trend-area" d={area} /><path className="dr-trend-line" d={line} /><circle cx="300" cy={12 + ((max - numbers[numbers.length - 1]) / range) * 66} r="3.5" /></svg><div className="dr-trend-legend"><span dir="ltr">שיא {formatPct(max)}</span><span>סף 85%</span><span dir="ltr">כעת {formatPct(numbers[numbers.length - 1])}</span></div></section>;
}

function LenderExposure({ baskets }) {
  const exposure = useMemo(() => Object.values(baskets.reduce((result, basket) => {
    const name = basket.lender || "לא צוין";
    result[name] = result[name] || { name, amount: 0 };
    result[name].amount += Number(basket.netDebt) || 0;
    return result;
  }, {})).sort((a, b) => b.amount - a.amount), [baskets]);
  const largest = exposure[0]?.amount || 1;
  return <section className="dr-side-card"><div className="dr-side-title"><h3>חשיפה לפי גוף מממן</h3></div><div className="dr-exposure-list">{exposure.map((item) => <div key={item.name}><span>{item.name}</span><i><b style={{ width: `${(item.amount / largest) * 100}%` }} /></i><strong dir="ltr">{formatILS(item.amount, true)}</strong></div>)}</div></section>;
}

function FreeSharesPanel({ data }) {
  return <section className="dr-side-card dr-free-shares"><div className="dr-side-title"><h3>מניות זמינות</h3><span dir="ltr">{formatILS(data.freeShareTotals?.freeValue, true)}</span></div><div className="dr-free-table-wrap"><table><thead><tr><th>ישות</th><th>סמל</th><th>לא משועבד</th><th>מוגבל</th><th>זמין</th><th>שווי זמין</th></tr></thead><tbody>{data.freeShares?.map((row, index) => <tr key={`${row.symbol}-${index}`}><td>{row.borrowerCompany}</td><td dir="ltr">{row.symbol}</td><td>{formatNumber(row.unpledgedShares ?? row.freeShares)}</td><td>{formatNumber(row.restrictedShares || 0)}</td><td>{formatNumber(row.freeShares)}</td><td dir="ltr">{formatILS(row.freeValue, true)}</td></tr>)}</tbody></table></div>{data.pledgeConstraintViolations?.map((item, index) => <p className="dr-constraint" key={index}>{item.details}</p>)}</section>;
}

export default function DailyReportPage() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentDate, setCurrentDate] = useState(() => dateString(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("severity");
  const [filter, setFilter] = useState("all");
  const today = dateString(new Date());
  const isLive = currentDate === today;

  useEffect(() => {
    setLoading(true);
    setError(null);
    const endpoint = currentDate === dateString(new Date()) ? "/reports/daily/live" : `/reports/daily/${currentDate}`;
    apiClient.get(endpoint).then((response) => setData(response.data)).catch((requestError) => setError(requestError.response?.status === 404 ? `אין דוח ליום ${currentDate}` : requestError.response?.data?.error || "טעינת הדוח נכשלה")).finally(() => setLoading(false));
  }, [currentDate]);

  useEffect(() => {
    apiClient.get(`/reports/daily/history?endDate=${currentDate}&days=30`).then((response) => setHistory(response.data?.history || [])).catch(() => setHistory([]));
  }, [currentDate]);

  const comparison = useMemo(() => {
    const previousPoints = history.filter((item) => item.date < currentDate);
    return previousPoints[previousPoints.length - 1];
  }, [history, currentDate]);
  const filteredBaskets = useMemo(() => {
    let items = [...(data?.baskets || [])];
    if (filter === "risk") items = items.filter((item) => ["BREACH", "WARNING", "NO_COLLATERAL", "NO_PRICE"].includes(item.ltvStatus));
    if (filter === "safe") items = items.filter((item) => ["SAFE", "RELEASE_ELIGIBLE"].includes(item.ltvStatus));
    if (sortBy === "severity") items.sort((a, b) => (severity[a.ltvStatus] ?? 5) - (severity[b.ltvStatus] ?? 5));
    if (sortBy === "distance") items.sort((a, b) => (a.pctToBreachLTV ?? Infinity) - (b.pctToBreachLTV ?? Infinity));
    if (sortBy === "debt") items.sort((a, b) => (b.netDebt || 0) - (a.netDebt || 0));
    return items;
  }, [data, filter, sortBy]);

  const handleDownload = useCallback(async () => {
    try {
      const response = await apiClient.get(`/reports/daily/${currentDate}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `daily-report-${currentDate}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      window.alert("PDF אינו זמין עדיין");
    }
  }, [currentDate]);

  return <main className="dr-page" dir="rtl">
    <NavBar currentDate={currentDate} generatedAt={data?.generatedAt} isLive={isLive} onDateChange={setCurrentDate} onDownload={handleDownload} />
    {loading && <div className="dr-loading">טוען דוח…</div>}
    {error && <div className="dr-error">{error}</div>}
    {data && !loading && <>
      <MarketTicker marketData={data.marketData} />
      <PortfolioMetrics summary={data.summary} freeShareTotals={data.freeShareTotals} comparison={comparison?.summary || comparison} />
      <div className="dr-content-grid">
        <BasketSection baskets={filteredBaskets} allBaskets={data.baskets || []} filter={filter} onFilterChange={setFilter} sortBy={sortBy} onSortChange={setSortBy} />
        <aside className="dr-sidebar">
          <AlertsPanel alerts={data.summary?.activeAlerts || []} />
          <LtvTrend history={history} currentSummary={data.summary} currentDate={currentDate} />
          <LenderExposure baskets={data.baskets || []} />
          <FreeSharesPanel data={data} />
        </aside>
      </div>
    </>}
  </main>;
}
